import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { withNamespaces } from 'react-i18next';

import { Form } from 'libs/forms';

import { useNoAuthRedirect } from 'apps/hooks';

import { ActionBar, Loading, SelectTags } from 'components';
import { WikiWrapper } from 'apps/components';

import { clientStore } from 'apps/client/app';
import { SetupProfileAppManager } from './appManager';
import { IndustryTags, UsingWikiReasons } from './config';

const SetupProfile: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const appManager = useMemo(() => new SetupProfileAppManager(null), []);
  const [industrySelectedKeys, setIndustrySelectedKeys] = useState([]);
  const [reasonsSelectedKeys, setReasonsSelectedKeys] = useState([]);
  useNoAuthRedirect();

  const getNewValues = (value: string, values: string[]) => {
    const hasChecked = values.some(v => v === value);
    return hasChecked ? values.filter(v => v !== value) : [...values, value];
  };

  const { isMobile } = clientStore;

  const queryProfileThenInitForm = async () => {
    setLoading(true);
    await clientStore.WhoAmI();
    appManager.initProfileForm();
    setLoading(false);
  };

  useEffect(() => {
    queryProfileThenInitForm();
  }, []);

  const rootClass = 'flex flex-col items-center justify-center flex-grow';

  const commonStyle = {
    margin: 5,
    fontSize: 14,
    padding: '6px 10px',
  };

  const activeStyle = {
    background: '#000',
    color: '#fff',
    ...commonStyle,
  };

  const inactiveStyle = {
    background: '#fff',
    color: '#000',
    ...commonStyle,
  };

  const setupList = [
    {
      title: '',
      content: (
        <div className='w-full'>
          {appManager.setupProfileStore && (
            <Form
              noBorder
              noPadding
              isSideBarForm
              formStore={appManager.setupProfileStore}
              buttonHandler={appManager.clickNext}
              actionWrapperStyle={{
                display: 'flex',
                width: '100%',
                justifyContent: 'center',
                margin: '1.6em auto',
              }}
              actionStyle={{
                marginTop: 0,
                background: '#000',
                borderColor: '#000',
              }}
              descriptionClassName='flex p-2 justify-center'
              socialWrapperClassName='flex w-full justify-center'
              formTitleStyle={{
                display: 'flex',
                margin: '0 auto',
                justifyContent: 'center',
              }}
            />
          )}
        </div>
      ),
    },
    {
      title: 'What industries do you work in?',
      description: (
        <div className='w-full mt-2 text-center text-black'>
          ({industrySelectedKeys.length}) Industries Selected
        </div>
      ),
      content: (
        <>
          <SelectTags
            activeStyle={activeStyle}
            inactiveStyle={inactiveStyle}
            sourceData={IndustryTags}
            selectKeys={industrySelectedKeys}
            onClickItem={v => {
              setIndustrySelectedKeys(getNewValues(v, industrySelectedKeys));
            }}
          />

          <ActionBar
            nextTestId='confirmProfilePage-next-button'
            onNext={appManager.clickNext}
            onBack={appManager.clickBack}
          />
        </>
      ),
    },
    {
      title: 'I will use Wikifactory for...',
      content: (
        <>
          <SelectTags
            sourceData={UsingWikiReasons}
            selectKeys={reasonsSelectedKeys}
            onClickItem={v => {
              setReasonsSelectedKeys(getNewValues(v, reasonsSelectedKeys));
            }}
            activeStyle={activeStyle}
            inactiveStyle={inactiveStyle}
          />

          <ActionBar
            nextTestId='confirmProfilePage-submit-button'
            onNext={() =>
              appManager.submitProfile({
                use_for: reasonsSelectedKeys,
                industries: industrySelectedKeys,
              })
            }
            loading={appManager.setupProfileStore?.posting}
            onBack={appManager.clickBack}
          />
        </>
      ),
    },
  ];

  const { content, title, description } = setupList[appManager.currentStep];

  return (
    <WikiWrapper>
      {loading ? (
        <Loading text='Loading' />
      ) : (
        <div className={` overflow-y-scroll ${rootClass}`}>
          {(title || description) && (
            <div className='my-8'>
              {title && (
                <h2 className='font-black text-black text-3xl text-center'>{title}</h2>
              )}
              {description}
            </div>
          )}
          {content}
          {!isMobile && (
            <div className='absolute bottom-4'>
              {setupList.map((item, idx) => (
                <p
                  key={item.title}
                  onClick={() => appManager.updateStep(idx)}
                  className={`inline-block cursor-pointer w-3 h-3 mx-1 rounded-full border ${
                    idx === appManager.currentStep ? 'bg-black' : 'bg-white'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </WikiWrapper>
  );
};

const SetupProfilePage = withNamespaces()(observer(SetupProfile));

export { SetupProfilePage };
